import { FC, useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import EditModal from 'components/UI/EditModal';
import Button from 'components/UI/Button';
import {
  Content,
  ButtonGrid,
  CheckboxItem,
  Description,
  TestContainer,
} from './styled';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import InputField from 'components/UI/InputField';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { Device, UpdateDeviceAttribute } from 'models';
import { useParams } from 'react-router';
import Loader from 'components/UI/Loader';
import { showErrorToast } from 'utils/toast';
import Checkbox from 'components/UI/Checkbox';
import {
  connectivityDetailsSelector,
  storeDeviceUpdates,
  updateDeviceSelector,
} from 'store/connectivityState/connectivityState';

type Props = {
  device: Device | null;
  open: boolean;
  onClose: () => void;
};

const EditDeviceModal: FC<Props> = ({ device, open, onClose }) => {
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const {
    handleSubmit,
    register,
    watch,
    setValue,
    formState: { errors },
  } = useForm();
  const { id } = useParams();
  const { error, isLoading } = useAppSelector(updateDeviceSelector);
  const { deviceInfo } = useAppSelector(connectivityDetailsSelector);

  // Watch
  const [isTestDevice] = watch([UpdateDeviceAttribute.IsTestDevice]);

  useEffect(() => {
    if (error) {
      const errorMessage =
        error instanceof Array ? error[0].title : (error as any).title;
      showErrorToast(
        `${intl.formatMessage({
          id: 'connectivity.testDeviceError',
        })}. ${errorMessage}`
      );
    }

    if (!isLoading && !error) {
      onClose();
    }
  }, [deviceInfo, dispatch, error, id, intl, onClose, isLoading]);

  // Submit
  const onSubmit: SubmitHandler<FieldValues> = useCallback(
    (values: FieldValues) => {
      if (id) {
        dispatch(
          storeDeviceUpdates(
            id,
            values.isTestDevice,
            values.description,
            device?.attributes.pnc || ''
          )
        );
      }
    },
    [dispatch, id, device]
  );

  const onChange = useCallback(() => {
    setValue(UpdateDeviceAttribute.IsTestDevice, !isTestDevice);
  }, [isTestDevice, setValue]);

  const loadingSpinner = useMemo(() => {
    if (isLoading) {
      return <Loader center />;
    }
  }, [isLoading]);

  if (deviceInfo === null) {
    return null;
  }
  return (
    <EditModal
      title="connectivity.device_edit_header"
      open={open}
      close={onClose}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Content>
          <TestContainer>
            <Description>
              <FormattedMessage id="connectivity.testDeviceExplanation" />
            </Description>
            <CheckboxItem>
              <Checkbox
                id="testBox"
                onChange={() => onChange()}
                register={register(UpdateDeviceAttribute.IsTestDevice)}
                defaultChecked={deviceInfo?.attributes.isTestDevice || false}
              />
              <label htmlFor={'testBox'}>
                <FormattedMessage id="connectivity.testDevice" />
              </label>
            </CheckboxItem>
            {isTestDevice && (
              <InputField
                description="connectivity.testDeviceDescription"
                error={errors.description}
                defaultValue={
                  deviceInfo?.attributes.testDeviceDescription || ''
                }
                register={register(UpdateDeviceAttribute.Description, {
                  required: {
                    value: true,
                    message: 'input.required',
                  },
                  maxLength: { value: 30, message: 'input.wrong_length' },
                })}
              />
            )}
          </TestContainer>
        </Content>
        <ButtonGrid>
          {loadingSpinner}
          <Button
            onClick={onClose}
            backgroundColor="transparent"
            color="primary"
          >
            <FormattedMessage id="modal.btn_close" />
          </Button>
          <Button submit>
            <FormattedMessage id="connectivity.btn_save" />
          </Button>
        </ButtonGrid>
      </form>
    </EditModal>
  );
};

export default EditDeviceModal;
